import secureStorage from "../../utils/secureStorage";
import { useState, useEffect, useMemo, useCallback } from "react";
import confetti from "canvas-confetti";
import { X, Sparkles, Heart, Star } from "lucide-react";

// Helper function to check if welcome should be shown
const shouldShowWelcomeScreen = (userId) => {
  if (!userId) return false;
  const welcomeKey = `welcome_shown_${userId}`;
  const hasSeenWelcome = localStorage.getItem(welcomeKey);
  const showAfterLogin = localStorage.getItem("show_welcome_after_login");
  return !hasSeenWelcome || showAfterLogin === "true";
};

// Disney-style welcome animation with confetti
// NOTE: Parent should render this with key={user?.id} to properly re-initialize on user change
export default function WelcomeAnimation({ user, onClose, showConfetti = true }) {
  // Calculate if we should show - evaluated once on mount (parent uses key to remount)
  const shouldShow = useMemo(() => shouldShowWelcomeScreen(user?.id), [user?.id]);
  const [show, setShow] = useState(shouldShow);
  const [animationStep, setAnimationStep] = useState(0);

  // Tema khas ibu bapa: hati & pelangi
  const isParent = !['admin', 'superadmin', 'guru', 'branch_manager'].includes(user?.role);

  // Generate sparkle positions once using useMemo
  const sparklePositions = useMemo(() => 
    [...Array(18)].map((_, i) => ({
      left: `${(i * 7) % 100}%`,
      top: `${(i * 11 + 5) % 90}%`,
      delay: `${(i * 0.15) % 2}s`,
      duration: `${3 + (i * 0.2) % 2}s`
    })), []
  );

  // Belon terapung naik dari bawah (design baharu: rainbow & balloon)
  const balloonData = useMemo(() => {
    const hues = [0, 40, 120, 200, 260, 320];
    return [...Array(8)].map((_, i) => ({
      left: `${(i * 13 + 4) % 92}%`,
      delay: `${(i * 0.6) % 4}s`,
      duration: `${6 + (i * 0.7) % 4}s`,
      size: `${2 + (i % 3) * 0.5}rem`,
      hue: hues[i % hues.length],
      sway: i % 2 === 0 ? 'balloon-rise-sway-left' : 'balloon-rise-sway-right',
    }));
  }, []);

  // Bunyi sambutan ceria - dijana dengan Web Audio API (tiada fail luaran)
  const playCheerfulSound = useCallback(() => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      if (ctx.state === 'suspended') ctx.resume();

      const master = ctx.createGain();
      master.gain.value = 0.18;
      master.connect(ctx.destination);

      // Arpeggio major ceria menaik: C5 - E5 - G5 - C6
      const notes = [523.25, 659.25, 783.99, 1046.5];
      notes.forEach((freq, i) => {
        const t = ctx.currentTime + i * 0.14;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(freq, t);
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.9, t + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
        osc.connect(gain);
        gain.connect(master);
        osc.start(t);
        osc.stop(t + 0.5);
      });

      // Kilauan "sparkle" lembut di hujung
      const sparkleTime = ctx.currentTime + 0.62;
      [1567.98, 2093.0].forEach((freq, i) => {
        const t = sparkleTime + i * 0.09;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, t);
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.5, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.6);
        osc.connect(gain);
        gain.connect(master);
        osc.start(t);
        osc.stop(t + 0.65);
      });

      // Tutup context selepas selesai untuk jimat sumber
      setTimeout(() => { try { ctx.close(); } catch (e) { /* noop */ } }, 2500);
    } catch (e) {
      // Autoplay disekat atau tiada sokongan audio - abaikan senyap
    }
  }, []);

  const launchConfetti = useCallback(() => {
    if (isParent) {
      // Confetti berbentuk hati & pelangi untuk ibu bapa
      const heartColors = ['#FF6B9D', '#FF4D6D', '#FF8FB3', '#FFC93C', '#6FE7DD'];
      let shapes = ['circle'];
      let scalar = 1.2;
      try {
        if (typeof confetti.shapeFromText === 'function') {
          shapes = [
            confetti.shapeFromText({ text: '❤️', scalar: 2 }),
            confetti.shapeFromText({ text: '🌈', scalar: 2 }),
            confetti.shapeFromText({ text: '🎈', scalar: 2 }),
            confetti.shapeFromText({ text: '💖', scalar: 2 }),
          ];
          scalar = 2;
        }
      } catch (e) { /* fallback bentuk biasa */ }

      confetti({
        particleCount: 60,
        spread: 100,
        origin: { y: 0.6 },
        colors: heartColors,
        shapes,
        scalar
      });

      setTimeout(() => {
        confetti({
          particleCount: 80,
          spread: 160,
          origin: { y: 0.5 },
          colors: heartColors,
          shapes,
          scalar
        });
      }, 1500);
      return;
    }

    const colors = ['#F04A83', '#7A5FB5', '#FFC93C', '#9178D6', '#FFD700', '#FF69B4'];
    let staffShapes = ['star', 'circle'];
    let staffScalar = 1.2;
    try {
      if (typeof confetti.shapeFromText === 'function') {
        staffShapes = [
          confetti.shapeFromText({ text: '🎈', scalar: 2 }),
          confetti.shapeFromText({ text: '🌈', scalar: 2 }),
          confetti.shapeFromText({ text: '⭐', scalar: 2 }),
        ];
        staffScalar = 2;
      }
    } catch (e) { /* fallback bentuk biasa */ }
    
    confetti({
      particleCount: 80,
      spread: 100,
      origin: { y: 0.6 },
      colors: colors,
      shapes: staffShapes,
      scalar: staffScalar
    });

    setTimeout(() => {
      confetti({
        particleCount: 100,
        spread: 160,
        origin: { y: 0.5 },
        colors: colors,
        shapes: staffShapes,
        scalar: staffScalar
      });
    }, 1500);
  }, [isParent]);

  // Handle animation steps and localStorage update
  useEffect(() => {
    if (!show || !user?.id) return;
    
    const welcomeKey = `welcome_shown_${user.id}`;
    
    // Mark as shown in localStorage
    localStorage.setItem(welcomeKey, "true");
    localStorage.removeItem("show_welcome_after_login");
    
    // Start animation steps
    const timers = [
      setTimeout(() => setAnimationStep(1), 300),
      setTimeout(() => setAnimationStep(2), 800),
      setTimeout(() => setAnimationStep(3), 1300),
      // Auto-dismiss after 4 seconds so it doesn't block the menu
      setTimeout(() => {
        setShow(false);
        if (onClose) onClose();
      }, 4000)
    ];
    
    if (showConfetti) {
      launchConfetti();
      playCheerfulSound();
    }
    
    return () => timers.forEach(clearTimeout);
  }, [show, user?.id, launchConfetti, playCheerfulSound, onClose, showConfetti]);

  // Simple close handler - works on both click and touch
  const handleClose = useCallback(() => {
    setShow(false);
    if (onClose) onClose();
  }, [onClose]);

  if (!show) return null;

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-md cursor-pointer bg-gradient-to-br ${
        isParent
          ? 'from-sky-300/95 via-sky-200/90 to-yellow-200/95'
          : 'from-sky-300/95 via-sky-200/90 to-yellow-200/95'
      }`}
      onClick={handleClose}
      data-testid="welcome-animation-overlay"
    >
      {/* Sparkles / Hati & Pelangi - completely non-interactive */}
      <div className="absolute inset-0 overflow-hidden" style={{ pointerEvents: 'none' }}>
        {sparklePositions.map((pos, i) => (
          <div
            key={i}
            className="absolute animate-float"
            style={{
              left: pos.left,
              top: pos.top,
              animationDelay: pos.delay,
              animationDuration: pos.duration,
              pointerEvents: 'none'
            }}
          >
            {isParent ? (
              i % 4 === 0 ? (
                <span className="text-2xl" style={{ opacity: 0.55 }}>🌈</span>
              ) : i % 4 === 1 ? (
                <Heart className="w-5 h-5 text-pink-200/70" fill="currentColor" />
              ) : i % 4 === 2 ? (
                <Heart className="w-4 h-4 text-red-300/60" fill="currentColor" />
              ) : (
                <Sparkles className="w-3 h-3 text-white/40" />
              )
            ) : i % 3 === 0 ? (
              <Star className="w-4 h-4 text-yellow-300/50" fill="currentColor" />
            ) : i % 3 === 1 ? (
              <Sparkles className="w-3 h-3 text-white/30" />
            ) : (
              <Heart className="w-3 h-3 text-pink-300/40" fill="currentColor" />
            )}
          </div>
        ))}
      </div>

      {/* Belon terapung naik - design baharu */}
      <div className="absolute inset-0 overflow-hidden" style={{ pointerEvents: 'none' }}>
        {balloonData.map((b, i) => (
          <span
            key={`balloon-${i}`}
            className={`absolute ${b.sway}`}
            style={{
              left: b.left,
              bottom: '-4rem',
              fontSize: b.size,
              animationDelay: b.delay,
              animationDuration: b.duration,
              filter: `hue-rotate(${b.hue}deg)`,
              pointerEvents: 'none',
            }}
          >
            🎈
          </span>
        ))}
        {/* Pelangi besar di bahagian atas */}
        <span
          className="absolute animate-rainbow-glow"
          style={{ top: '6%', left: '8%', fontSize: '3.5rem', pointerEvents: 'none' }}
        >
          🌈
        </span>
        <span
          className="absolute animate-rainbow-glow"
          style={{ top: '10%', right: '8%', fontSize: '2.8rem', animationDelay: '0.8s', pointerEvents: 'none' }}
        >
          🌈
        </span>
      </div>

      {/* Main content container */}
      <div className="relative text-center px-6 max-w-sm w-full mx-4">
        
        {/* Close button - appropriately sized for mobile */}
        <div 
          className="absolute -top-2 -right-2 z-[10000]"
          style={{ pointerEvents: 'auto' }}
        >
          <button
            type="button"
            onClick={handleClose}
            className="w-10 h-10 bg-white/60 backdrop-blur rounded-full flex items-center justify-center text-gray-700 shadow-lg border border-white/80 active:bg-white/80 active:scale-95 transition-all"
            style={{ 
              WebkitTapHighlightColor: 'rgba(255,255,255,0.3)',
              touchAction: 'manipulation'
            }}
            aria-label="Tutup"
            data-testid="welcome-close-btn"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>

        {/* Logo */}
        <div className={`transition-all duration-700 ${animationStep >= 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`} style={{ pointerEvents: 'none' }}>
          <div className="w-28 h-28 mx-auto mb-5 relative">
            <div className="absolute inset-0 bg-white rounded-full shadow-2xl"></div>
            <div className={`absolute inset-2 rounded-full flex items-center justify-center bg-gradient-to-br ${
              isParent ? 'from-pink-400 to-rose-500' : 'from-pink-400 to-purple-500'
            }`}>
              <span className="text-5xl">{isParent ? '🌈' : '🎉'}</span>
            </div>
            {isParent && (
              <span className="absolute -top-1 -right-1 text-3xl animate-heartbeat" style={{ pointerEvents: 'none' }}>💖</span>
            )}
          </div>
        </div>

        {/* Welcome text */}
        <div className={`transition-all duration-700 ${animationStep >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ pointerEvents: 'none' }}>
          <h1 className="text-3xl font-black text-sky-900 mb-3 drop-shadow-sm" style={{ fontFamily: "'Bungee', sans-serif" }}>
            Selamat Datang!
          </h1>
          <p className="text-lg text-sky-800 mb-2 font-medium">
            Hai, <span className="text-pink-500 font-bold">{user?.name || 'Pengguna'}</span>! 
          </p>
        </div>

        {/* Message box */}
        <div className={`transition-all duration-700 ${animationStep >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-5 mt-5 border border-white/80" style={{ pointerEvents: 'none' }}>
            {['admin', 'superadmin', 'guru', 'branch_manager'].includes(user?.role) ? (
              <>
                <p className="text-sky-900 text-base leading-relaxed">
                  <Star className="inline w-5 h-5 text-amber-500 mr-1" fill="currentColor" />
                  Selamat Bekerja dan menjalankan amanah dengan baik di
                </p>
                <p className="text-2xl font-black text-pink-500 mt-2 drop-shadow-sm" style={{ fontFamily: "'Bungee', sans-serif" }}>
                  TASKA UMMU QASEH
                </p>
                <p className="text-sky-800/80 text-sm mt-2">
                  Pemantauan & Dedikasi Sepanjang Masa 🌟
                </p>
              </>
            ) : (
              <>
                <p className="text-sky-900 text-base leading-relaxed">
                  <Heart className="inline w-5 h-5 text-pink-500 mr-1" fill="currentColor" />
                  Terima kasih kerana memilih
                </p>
                <p className="text-2xl font-black text-pink-500 mt-2 drop-shadow-sm" style={{ fontFamily: "'Bungee', sans-serif" }}>
                  TASKA UMMU QASEH
                </p>
                <p className="text-sky-800/80 text-sm mt-2">
                  Because We Love 💕
                </p>
              </>
            )}
          </div>

          {/* Main button - LARGE and EASY to tap */}
          <div className="mt-6" style={{ pointerEvents: 'auto' }}>
            <button
              type="button"
              onClick={handleClose}
              className="w-full py-5 px-8 bg-white text-purple-600 font-bold text-xl rounded-full shadow-2xl border-2 border-purple-200 active:bg-purple-100 active:scale-98 transition-all"
              style={{ 
                WebkitTapHighlightColor: 'rgba(147, 51, 234, 0.2)',
                touchAction: 'manipulation',
                minHeight: '64px'
              }}
              data-testid="welcome-start-btn"
            >
              <Sparkles className="inline w-6 h-6 mr-2" />
              Mula Sekarang!
            </button>
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.5; }
          50% { transform: translateY(-15px) rotate(180deg); opacity: 0.8; }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          25% { transform: scale(1.25); }
          50% { transform: scale(1); }
          75% { transform: scale(1.15); }
        }
        .animate-heartbeat {
          animation: heartbeat 1.4s ease-in-out infinite;
        }
        @keyframes balloon-rise-left {
          0% { transform: translateY(0) translateX(0) rotate(-4deg); opacity: 0; }
          10% { opacity: 0.9; }
          50% { transform: translateY(-45vh) translateX(-18px) rotate(4deg); }
          100% { transform: translateY(-110vh) translateX(10px) rotate(-4deg); opacity: 0.7; }
        }
        @keyframes balloon-rise-right {
          0% { transform: translateY(0) translateX(0) rotate(4deg); opacity: 0; }
          10% { opacity: 0.9; }
          50% { transform: translateY(-45vh) translateX(18px) rotate(-4deg); }
          100% { transform: translateY(-110vh) translateX(-10px) rotate(4deg); opacity: 0.7; }
        }
        .balloon-rise-sway-left {
          animation: balloon-rise-left 7s linear infinite;
        }
        .balloon-rise-sway-right {
          animation: balloon-rise-right 7s linear infinite;
        }
        @keyframes rainbow-glow {
          0%, 100% { transform: scale(1) translateY(0); opacity: 0.75; }
          50% { transform: scale(1.12) translateY(-6px); opacity: 1; }
        }
        .animate-rainbow-glow {
          animation: rainbow-glow 3.2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
